import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetServiceCategoriesQuery,
  useGetHomepageSettingsQuery,
  useGetSpecialOffersQuery,
} from "../redux/services/api";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";

const MobileDrawer = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { t, isArabic } = useTranslation();
  const { toggleLanguage, language, getLocalizedPath } = useLanguage();
  const { toggleTheme, isLightMode } = useTheme();
  const [activeTab, setActiveTab] = useState("services");

  const { data: categoriesData } = useGetServiceCategoriesQuery();
  const { data: settingsResponse } = useGetHomepageSettingsQuery();
  const { data: offersData } = useGetSpecialOffersQuery();

  const categories =
    categoriesData?.categories?.filter((cat) => cat && cat.is_active) || [];
  const settings = settingsResponse?.settings || {};
  const logoUrl = settings?.branding?.logo || null;

  // Check if there are any active offers
  const activeOffers =
    offersData?.offers?.filter((offer) => offer && offer.is_active) || [];
  const hasOffers = activeOffers.length > 0;

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleCategoryClick = (categoryId) => {
    navigate(getLocalizedPath(`/services?category=${categoryId}`));
    onClose();
  };

  const handleNavigation = (section) => {
    if (section === "home") {
      navigate(getLocalizedPath("/"));
    } else if (section === "services") {
      navigate(getLocalizedPath("/services"));
    } else if (section === "offers") {
      navigate(getLocalizedPath("/offers"));
    } else {
      navigate(getLocalizedPath(`/#${section}`));
    }
    onClose();
  };

  const handlePhoneClick = () => {
    if (settings?.contact?.phone) {
      if (typeof window.gtag_report_conversion === "function") {
        window.gtag_report_conversion("tel:+971502080276");
      }
      window.open(`tel:${settings.contact.phone}`, "_self");
    }
  };

  const handleWhatsAppClick = () => {
    if (settings?.contact?.whatsapp) {
      // Check if it's already a WhatsApp URL
      if (
        settings.contact.whatsapp.startsWith("https://wa.me/") ||
        settings.contact.whatsapp.startsWith("whatsapp://")
      ) {
        window.open(settings.contact.whatsapp, "_blank", "noopener,noreferrer");
      } else {
        // If it's a regular URL, try to convert it to WhatsApp format
        // Extract phone number from URL if possible
        let phoneNumber = settings.contact.whatsapp;

        // Remove common URL parts
        phoneNumber = phoneNumber.replace(
          /^https?:\/\/(wa\.me\/|api\.whatsapp\.com\/send\?phone=)/,
          ""
        );
        phoneNumber = phoneNumber.replace(/[^\d+]/g, ""); // Keep only digits and +

        // Try WhatsApp app protocol first (for mobile)
        const whatsappAppUrl = `whatsapp://send?phone=${phoneNumber}`;
        const whatsappWebUrl = `https://wa.me/${phoneNumber}`;

        // Try to open WhatsApp app first
        const appWindow = window.open(
          whatsappAppUrl,
          "_blank",
          "noopener,noreferrer"
        );

        // If app doesn't open (fallback to web), try web version after a short delay
        setTimeout(() => {
          if (!appWindow || appWindow.closed) {
            window.open(whatsappWebUrl, "_blank", "noopener,noreferrer");
          }
        }, 1000);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="mobile-drawer-overlay" onClick={onClose}></div>

      {/* Drawer */}
      <div
        className={`mobile-drawer ${isOpen ? "open" : ""}`}
        dir={isArabic ? "rtl" : "ltr"}
      >
        {/* Header */}
        <div className="drawer-header">
          <button
            className="drawer-close-btn"
            onClick={onClose}
            aria-label="Close menu"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              width="24"
              height="24"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <div className="drawer-logo">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={settings?.branding?.companyName || "Logo"}
              />
            ) : (
              <div className="drawer-logo-placeholder">S2</div>
            )}
          </div>

          <div className="drawer-header-icons">
            {settings?.contact?.phone && (
              <button
                className="drawer-icon-btn"
                onClick={handlePhoneClick}
                aria-label="Phone"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="20"
                  height="20"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
              </button>
            )}
            {settings?.contact?.whatsapp && (
              <button
                className="drawer-icon-btn"
                onClick={handleWhatsAppClick}
                aria-label="WhatsApp"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                >
                  <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                  <g
                    id="SVGRepo_tracerCarrier"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></g>
                  <g id="SVGRepo_iconCarrier">
                    <path
                      d="M6.014 8.00613C6.12827 7.1024 7.30277 5.87414 8.23488 6.01043L8.23339 6.00894C9.14051 6.18132 9.85859 7.74261 10.2635 8.44465C10.5504 8.95402 10.3641 9.4701 10.0965 9.68787C9.7355 9.97883 9.17099 10.3803 9.28943 10.7834C9.5 11.5 12 14 13.2296 14.7107C13.695 14.9797 14.0325 14.2702 14.3207 13.9067C14.5301 13.6271 15.0466 13.46 15.5548 13.736C16.3138 14.178 17.0288 14.6917 17.69 15.27C18.0202 15.546 18.0977 15.9539 17.8689 16.385C17.4659 17.1443 16.3003 18.1456 15.4542 17.9421C13.9764 17.5868 8 15.27 6.08033 8.55801C5.97237 8.24048 5.99955 8.12044 6.014 8.00613Z"
                      fill="currentColor"
                    ></path>
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12 23C10.7764 23 10.0994 22.8687 9 22.5L6.89443 23.5528C5.56462 24.2177 4 23.2507 4 21.7639V19.5C1.84655 17.492 1 15.1767 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23ZM6 18.6303L5.36395 18.0372C3.69087 16.4772 3 14.7331 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C11.0143 21 10.552 20.911 9.63595 20.6038L8.84847 20.3397L6 21.7639V18.6303Z"
                      fill="currentColor"
                    ></path>
                  </g>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="drawer-tabs">
          <button
            className={`drawer-tab ${activeTab === "services" ? "active" : ""}`}
            onClick={() => setActiveTab("services")}
          >
            {t("nav.ourServices").toUpperCase()}
          </button>
          <button
            className={`drawer-tab ${activeTab === "explore" ? "active" : ""}`}
            onClick={() => setActiveTab("explore")}
          >
            {isArabic ? "استكشف" : "EXPLORE"}
          </button>
        </div>

        {/* Content */}
        <div className="drawer-content">
          {activeTab === "services" && (
            <div className="drawer-services">
              <a
                href={getLocalizedPath("/services")}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigation("services");
                }}
                className="drawer-service-item"
              >
                {t("nav.ourServices")}
              </a>
              {categories.map((category) => (
                <button
                  key={category.id}
                  className="drawer-service-item"
                  onClick={() => handleCategoryClick(category.id)}
                >
                  {isArabic && category.title_ar
                    ? category.title_ar
                    : category.title}
                </button>
              ))}
            </div>
          )}

          {activeTab === "explore" && (
            <div className="drawer-explore">
              <button
                className="drawer-nav-item"
                onClick={() => handleNavigation("home")}
              >
                {t("nav.home")}
              </button>
              <button
                className="drawer-nav-item"
                onClick={() => handleNavigation("about")}
              >
                {t("nav.aboutUs")}
              </button>
              {hasOffers && (
                <button
                  className="drawer-nav-item"
                  onClick={() => handleNavigation("offers")}
                >
                  {t("nav.specialOffers")}
                </button>
              )}
              <button
                className="drawer-nav-item"
                onClick={() => handleNavigation("contact")}
              >
                {t("nav.contactUs")}
              </button>
            </div>
          )}
        </div>

        {/* Bottom Actions */}
        <div className="drawer-bottom">
          <div className="drawer-bottom-section">
            <span className="drawer-bottom-label">
              {isArabic ? "اللغة" : "LANGUAGE"}
            </span>
            <div className="drawer-bottom-actions">
              <button
                className={`drawer-language-btn ${
                  language === "en" ? "active" : ""
                }`}
                onClick={() => {
                  if (language !== "en") toggleLanguage();
                }}
              >
                <span>English</span>
              </button>
              <button
                className={`drawer-language-btn ${
                  language === "ar" ? "active" : ""
                }`}
                onClick={() => {
                  if (language !== "ar") toggleLanguage();
                }}
              >
                <span>العربية</span>
              </button>
            </div>
          </div>

          <button className="drawer-theme-btn" onClick={toggleTheme}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              width="20"
              height="20"
            >
              {isLightMode ? (
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
              ) : (
                <>
                  <circle cx="12" cy="12" r="5"></circle>
                  <line x1="12" y1="1" x2="12" y2="3"></line>
                  <line x1="12" y1="21" x2="12" y2="23"></line>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                  <line x1="1" y1="12" x2="3" y2="12"></line>
                  <line x1="21" y1="12" x2="23" y2="12"></line>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                </>
              )}
            </svg>
            <span>
              {isLightMode
                ? isArabic
                  ? "الوضع الداكن"
                  : "Dark Mode"
                : isArabic
                ? "الوضع الفاتح"
                : "Light Mode"}
            </span>
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileDrawer;
